import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";
import { rhm, apiError } from "../api";
import { Btn, Field, Input, Textarea } from "../ui";
import { REGISTRY, SECTION_TYPES } from "../../site/registry";

// ---------------------------------------------------------------- dialogs
export const Shell = ({ title, children, onClose, testId, wide }) => (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
        data-testid={testId} onClick={onClose}>
        <div className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} bg-bg1 border border-white/12 p-6 my-10`}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4 mb-5">
                <h3 className="font-display text-2xl tracking-tight">{title}</h3>
                <button onClick={onClose} data-testid={`${testId}-close`}
                    className="w-9 h-9 border border-white/15 flex items-center justify-center text-zinc-400 hover:text-white">
                    <X className="w-4 h-4" />
                </button>
            </div>
            {children}
        </div>
    </div>
);

export const InlineEditor = ({ payload, onSave, onClose }) => {
    const [val, setVal] = useState(payload.value || "");
    return (
        <Shell title="Edit text" onClose={onClose} testId="inline-editor">
            <div className="mono text-[10px] tracking-[0.16em] text-zinc-500 mb-2">{payload.field.toUpperCase()}</div>
            {payload.multiline
                ? <Textarea rows={5} value={val} data-testid="inline-editor-input" onChange={(e) => setVal(e.target.value)} />
                : <Input value={val} data-testid="inline-editor-input" onChange={(e) => setVal(e.target.value)} />}
            <div className="mt-5 flex justify-end gap-2">
                <Btn variant="ghost" onClick={onClose}>CANCEL</Btn>
                <Btn onClick={() => onSave(val)} data-testid="inline-editor-save">APPLY</Btn>
            </div>
        </Shell>
    );
};

export const AddSection = ({ onPick, onPickTemplate, onClose, types = null }) => {
    const qc = useQueryClient();
    const { data: templates = [] } = useQuery({
        queryKey: ["section-templates"],
        queryFn: async () => (await rhm.get("/site/section-templates")).data,
    });
    const allowed = types
        ? SECTION_TYPES.filter((s) => types.includes(s.type))
        : SECTION_TYPES;
    const presets = types ? templates.filter((t) => types.includes(t.section_type)) : templates;
    const remove = async (t) => {
        if (!window.confirm(`Delete the template "${t.name}"? Sections already added stay as they are.`)) return;
        try {
            await rhm.delete(`/site/section-templates/${t.id}`);
            toast.success("Template deleted");
            qc.invalidateQueries({ queryKey: ["section-templates"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <Shell title="Add a section" onClose={onClose} testId="add-section-dialog" wide>
            {!!presets.length && (
                <div className="mb-6" data-testid="template-list">
                    <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">YOUR SAVED TEMPLATES</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {presets.map((t) => (
                            <div key={t.id} className="flex items-stretch border border-gold/30 hover:border-gold transition-colors">
                                <button onClick={() => onPickTemplate(t)} data-testid={`add-template-${t.id}`}
                                    className="flex-1 text-left p-4">
                                    <div className="font-display text-lg uppercase tracking-tight">{t.name}</div>
                                    <p className="text-xs text-zinc-400 mt-1">
                                        {REGISTRY[t.section_type]?.label || t.section_type}
                                        {t.created_by ? ` · saved by ${t.created_by}` : ""}
                                    </p>
                                </button>
                                <button onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}
                                    aria-label={`Delete ${t.name}`}
                                    className="px-3 text-zinc-500 hover:text-red-400 border-l border-white/10">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="mono text-[10px] tracking-[0.2em] text-zinc-500 mb-3">SECTION TYPES</div>
            <div className="grid sm:grid-cols-2 gap-3">
                {allowed.map((s) => (
                    <button key={s.type} onClick={() => onPick(s.type)} data-testid={`add-section-${s.type}`}
                        className="text-left p-4 border border-white/12 hover:border-gold transition-colors">
                        <div className="font-display text-lg uppercase tracking-tight">{s.label}</div>
                        <p className="text-xs text-zinc-400 mt-1">{s.description}</p>
                    </button>
                ))}
            </div>
        </Shell>
    );
};

export const SaveTemplateDialog = ({ section, onClose }) => {
    const qc = useQueryClient();
    const [name, setName] = useState(section.name || REGISTRY[section.type]?.label || "My section");
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            await rhm.post("/site/section-templates", {
                name, section_type: section.type, props: section.props || {},
            });
            toast.success(`"${name}" saved — add it to any page from Add section`);
            qc.invalidateQueries({ queryKey: ["section-templates"] });
            onClose();
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };
    return (
        <Shell title="Save as template" onClose={onClose} testId="save-template-dialog">
            <p className="text-sm text-zinc-400">
                This saves the section with everything you've written and chosen. Drop it onto any page later and edit
                the copy there — the template itself stays unchanged.
            </p>
            <Field label="Template name" className="mt-4">
                <Input value={name} data-testid="save-template-name" onChange={(e) => setName(e.target.value)} />
            </Field>
            <div className="flex gap-3 mt-5">
                <Btn onClick={save} disabled={busy || !name.trim()} data-testid="save-template-submit">
                    {busy ? "SAVING…" : "SAVE TEMPLATE"}
                </Btn>
                <Btn variant="ghost" onClick={onClose}>CANCEL</Btn>
            </div>
        </Shell>
    );
};

export const PublishDialog = ({ onClose, onPublish, slug = "home", globals = false }) => {
    const { data } = useQuery({
        queryKey: ["site-changes", slug],
        queryFn: async () => (await rhm.get(`/site/pages/${slug}/changes`)).data,
        enabled: !globals,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: "always",
    });
    return (
        <Shell title="Publish changes" onClose={onClose} testId="publish-dialog">
            <p className="text-sm text-zinc-400">These changes will go live on rehealthme.com immediately after publishing.</p>
            <ul className="mt-4 space-y-2 text-sm text-zinc-200" data-testid="publish-changes">
                {(globals
                    ? ["Header, menu, announcement bar and footer changes will apply to every page"]
                    : data?.changes || ["Loading…"]).map((c, i) => <li key={i}>• {c}</li>)}
            </ul>
            <div className="mt-6 flex justify-end gap-2">
                <Btn variant="ghost" onClick={onClose} data-testid="publish-cancel">CANCEL</Btn>
                <Btn onClick={onPublish} data-testid="publish-confirm">PUBLISH</Btn>
            </div>
        </Shell>
    );
};

export const VersionHistory = ({ onClose, onRestored, canRestore, slug = "home" }) => {
    const { data: versions = [] } = useQuery({
        queryKey: ["site-versions", slug],
        queryFn: async () => (await rhm.get(`/site/pages/${slug}/versions`)).data,
    });
    const restore = async (v) => {
        try {
            await rhm.post(`/site/pages/${slug}/versions/${v}/restore`);
            const { data } = await rhm.get(`/site/pages/${slug}`);
            toast.success(`Restored version ${v} and published it`);
            onRestored(data.draft?.sections || []);
        } catch (e) { toast.error(apiError(e)); }
    };
    return (
        <Shell title="Version history" onClose={onClose} testId="versions-dialog" wide>
            {versions.length === 0 && <div className="text-sm text-zinc-500">Nothing published yet.</div>}
            <ul className="space-y-2">
                {versions.map((v) => (
                    <li key={v.id} className="border border-white/10 p-4" data-testid={`version-${v.version}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="font-display text-lg tracking-tight">Version {v.version}</div>
                                <div className="mono text-[9px] tracking-[0.14em] text-zinc-500">
                                    {v.created_at?.slice(0, 16).replace("T", " ")} · {v.user_name}
                                </div>
                            </div>
                            {canRestore && (
                                <Btn variant="ghost" onClick={() => restore(v.version)} data-testid={`restore-${v.version}`}>
                                    RESTORE & PUBLISH
                                </Btn>
                            )}
                        </div>
                        <ul className="mt-2 text-xs text-zinc-400 space-y-1">
                            {(v.changes || []).map((c, i) => <li key={i}>• {c}</li>)}
                        </ul>
                    </li>
                ))}
            </ul>
        </Shell>
    );
};
